import UtilityBar from '../components/layout/UtilityBar';
import Nav from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import QRCode from '../components/ui/QRCode';

/**
 * Public certificate page — what a learner shares with employers, parents,
 * social networks. The certificate itself + share/verify panels + badges.
 */
export default function Certificate() {
  return (
    <div style={{ background: 'var(--bg-cream)' }}>
      <UtilityBar />
      <Nav variant="light" />

      <section className="relative" style={{ padding: '48px 0 80px' }}>
        <div className="absolute"
          style={{ top: 0, left: 0, right: 0, height: 360, background: 'linear-gradient(180deg, var(--navy) 0%, var(--navy-3) 100%)' }} />

        <div className="lc-container relative" style={{ zIndex: 1 }}>
          <div className="flex justify-between items-center mb-7">
            <div className="flex items-center gap-2" style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
              <a href="#" style={{ color: 'rgba(255,255,255,0.7)' }}>Certificates</a>
              <span>›</span>
              <span className="text-white font-semibold">AI Literacy 101 · I. Ahmed</span>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full font-bold tracking-wider"
              style={{ padding: '8px 14px', background: 'rgba(34,181,114,0.18)', border: '1px solid rgba(34,181,114,0.4)', fontSize: 12, color: '#7be3b0' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20,6 9,17 4,12" /></svg>
              VERIFIED CERTIFICATE
            </div>
          </div>

          <h1 className="text-white" style={{ fontSize: 44, maxWidth: 800, letterSpacing: '-0.02em' }}>
            Imran Ahmed completed <em className="italic" style={{ color: 'var(--yellow)' }}>AI Literacy 101</em>.
          </h1>
          <p className="mt-3" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 17, maxWidth: 700 }}>
            Issued by Lincoln School in partnership with the World Economic Forum · Verified on the public registry · ID #LIN-2026-08412
          </p>

          <div className="grid mt-12" style={{ gridTemplateColumns: '1.7fr 1fr', gap: 32 }}>
            <Certificatum />

            <div className="flex flex-col gap-4">
              {/* Share card */}
              <div className="bg-white" style={{ borderRadius: 16, padding: 24, boxShadow: 'var(--shadow-md)' }}>
                <h3 style={{ fontSize: 20 }}>Share your certificate</h3>
                <p className="mt-1.5 mb-4" style={{ fontSize: 13, color: 'var(--muted)' }}>Show the world what you&apos;ve learned.</p>
                <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    { label: 'LinkedIn',  icon: 'in', bg: '#0a66c2',         color: '#fff' },
                    { label: 'Twitter / X', icon: '𝕏', bg: '#000',             color: '#fff' },
                    { label: 'Facebook',  icon: 'f',  bg: '#1877f2',         color: '#fff' },
                    { label: 'WhatsApp',  icon: '💬', bg: '#25d366',         color: '#fff' },
                    { label: 'Email',     icon: '✉',  bg: 'var(--bg-soft)',  color: 'var(--ink)' },
                    { label: 'Copy link', icon: '🔗', bg: 'var(--bg-soft)',  color: 'var(--ink)' },
                  ].map((s) => (
                    <button key={s.label} type="button" className="flex items-center gap-2 font-semibold"
                      style={{ padding: '10px 12px', borderRadius: 10, background: s.bg, color: s.color, fontSize: 13 }}>
                      <span className="inline-flex items-center justify-center rounded-full font-bold"
                        style={{ width: 22, height: 22, background: s.color === '#fff' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.06)', fontSize: 12 }}>{s.icon}</span>
                      {s.label}
                    </button>
                  ))}
                </div>
                <div className="mt-4 pt-4 flex gap-2" style={{ borderTop: '1px solid var(--line)' }}>
                  <button type="button" className="lc-btn ghost flex-1" style={{ padding: '10px 14px', fontSize: 13 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7,10 12,15 17,10" /><line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    PDF
                  </button>
                  <button type="button" className="lc-btn ghost flex-1" style={{ padding: '10px 14px', fontSize: 13 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6,9 6,2 18,2 18,9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
                    Print
                  </button>
                </div>
              </div>

              {/* Verify card */}
              <div className="bg-white" style={{ borderRadius: 16, padding: 24, boxShadow: 'var(--shadow-md)' }}>
                <h3 style={{ fontSize: 20 }}>Verify authenticity</h3>
                <p className="mt-1.5 mb-4" style={{ fontSize: 13, color: 'var(--muted)' }}>Anyone can scan the QR or paste the link to verify.</p>
                <div className="font-mono leading-snug mb-3" style={{ background: 'var(--bg-soft)', borderRadius: 10, padding: 12, fontSize: 11, color: 'var(--ink-2)', wordBreak: 'break-all' }}>
                  https://lincoln.school/verify/<br />LIN-2026-08412-AI101
                </div>
                <ul className="list-none p-0 m-0 flex flex-col gap-2.5">
                  {['Issued: March 14, 2026', 'Issuer: Lincoln School Oy (Finland)', 'Co-signed: World Economic Forum', 'Blockchain-anchored on Ethereum'].map((d) => (
                    <li key={d} className="flex items-center gap-2" style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--green-2)" strokeWidth="3"><polyline points="20,6 9,17 4,12" /></svg>
                      {d}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="text-white" style={{ background: 'var(--navy)', borderRadius: 16, padding: 24 }}>
                <h3 className="text-white" style={{ fontSize: 20 }}>Add to your LinkedIn</h3>
                <p className="mt-1.5 mb-4" style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>One-click integration with LinkedIn&apos;s certifications section.</p>
                <button type="button" className="lc-btn yellow w-full">Add to LinkedIn profile</button>
              </div>
            </div>
          </div>

          {/* Badges */}
          <div className="mt-16">
            <div className="flex justify-between items-end mb-6">
              <div>
                <span className="lc-eyebrow">Imran&apos;s badges</span>
                <h2 className="mt-3" style={{ fontSize: 32 }}>Other things Imran has earned.</h2>
              </div>
              <a href="#" className="lc-link" style={{ color: 'var(--ink-2)' }}>View full profile →</a>
            </div>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(6, 1fr)', gap: 16 }}>
              {[
                { name: 'AI Literacy 101',         earned: true,  color: 'var(--yellow)',  icon: '🤖' },
                { name: 'Voice Conversationalist', earned: true,  color: 'var(--green)',   icon: '🎙' },
                { name: 'Quiz Master',             earned: true,  color: 'var(--blue)',    icon: '📝' },
                { name: '12-Day Streak',           earned: true,  color: '#c97864',        icon: '🔥' },
                { name: 'Multilingual Learner',    earned: true,  color: '#7a3a8a',        icon: '🌐' },
                { name: 'Calculus I',              earned: false, color: 'var(--muted-2)', icon: '📐' },
              ].map((b) => (
                <div key={b.name} className="relative bg-white text-center"
                  style={{ borderRadius: 14, padding: 18, border: '1px solid var(--line)', opacity: b.earned ? 1 : 0.5 }}>
                  <div className="mx-auto flex items-center justify-center rounded-full border-2 border-white"
                    style={{
                      width: 64, height: 64,
                      background: `radial-gradient(circle at 35% 30%, ${b.color}, ${b.color}aa)`,
                      fontSize: 28,
                      boxShadow: b.earned ? `0 8px 20px ${b.color}55` : 'none',
                      outline: `2px solid ${b.color}33`,
                      filter: b.earned ? 'none' : 'grayscale(0.9)',
                    }}>{b.icon}</div>
                  <div className="font-bold mt-3" style={{ fontSize: 12, color: 'var(--ink)' }}>{b.name}</div>
                  <div className="mt-1" style={{ fontSize: 10, color: 'var(--muted)' }}>{b.earned ? 'Earned ✓' : 'Locked'}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What this proves */}
      <section className="bg-white" style={{ padding: '80px 0' }}>
        <div className="lc-container">
          <div className="text-center mb-12">
            <span className="lc-eyebrow">What this certificate proves</span>
            <h2 className="mt-3.5">Skills demonstrated.</h2>
          </div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {[
              { title: 'Understands AI fundamentals', desc: 'Neural networks, training, inference, transformers.' },
              { title: 'Identifies AI bias & risks',  desc: 'Recognizes patterns of model failure and ethical implications.' },
              { title: 'Uses voice AI tools',         desc: 'Conversational interaction with Lincoln, prompt construction.' },
              { title: 'Communicates about AI',       desc: 'Can explain concepts to peers in plain language.' },
            ].map((s) => (
              <div key={s.title} style={{ padding: 20, background: 'var(--bg-cream)', borderRadius: 14, border: '1px solid var(--line)' }}>
                <div className="flex items-center justify-center mb-3.5"
                  style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--yellow)' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--navy)" strokeWidth="2.5"><polyline points="20,6 9,17 4,12" /></svg>
                </div>
                <div className="font-bold" style={{ fontSize: 15, color: 'var(--ink)' }}>{s.title}</div>
                <div className="mt-1.5" style={{ fontSize: 13, color: 'var(--muted)' }}>{s.desc}</div>
              </div>
            ))}
          </div>

          {/* Mastery breakdown */}
          <div className="mt-12" style={{ background: 'var(--bg-soft)', padding: 32, borderRadius: 20 }}>
            <div className="flex justify-between items-center mb-5">
              <h3 style={{ fontSize: 22 }}>Mastery breakdown</h3>
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>Based on 84 quiz questions over 24 lessons</span>
            </div>
            <div className="flex flex-col gap-3.5">
              {[
                { topic: 'AI Fundamentals',           score: 92 },
                { topic: 'Machine Learning Basics',   score: 88 },
                { topic: 'Neural Networks',           score: 95 },
                { topic: 'Bias & Ethics',             score: 79 },
                { topic: 'Voice & Conversational AI', score: 96 },
                { topic: 'AI in the Real World',      score: 84 },
              ].map((m) => (
                <div key={m.topic} className="grid items-center" style={{ gridTemplateColumns: '200px 1fr 50px', gap: 16 }}>
                  <div className="font-semibold" style={{ fontSize: 14, color: 'var(--ink)' }}>{m.topic}</div>
                  <div className="bg-white overflow-hidden" style={{ height: 12, borderRadius: 6, border: '1px solid var(--line)' }}>
                    <div style={{ width: `${m.score}%`, height: '100%', background: 'linear-gradient(90deg, var(--yellow), var(--green))', borderRadius: 6 }} />
                  </div>
                  <div className="font-bold text-right" style={{ fontSize: 14, color: 'var(--ink)' }}>{m.score}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ─── The certificate document ────────────────────────────────────────────── */
function Certificatum() {
  return (
    <div className="relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #faf7ef 0%, #fff 60%)',
        borderRadius: 8,
        padding: 48,
        boxShadow: '0 30px 80px rgba(11,31,56,0.25), 0 0 0 1px rgba(11,31,56,0.08)',
        aspectRatio: '1.41 / 1',
      }}>
      <div className="absolute pointer-events-none" style={{ inset: 16, border: '2px solid var(--yellow)', borderRadius: 4 }} />
      <div className="absolute pointer-events-none" style={{ inset: 22, border: '1px solid rgba(11,31,56,0.15)', borderRadius: 2 }} />

      {[
        { top: 28, left: 28, rotate: 0 },
        { top: 28, right: 28, rotate: 90 },
        { bottom: 28, right: 28, rotate: 180 },
        { bottom: 28, left: 28, rotate: 270 },
      ].map((p, i) => (
        <svg key={i} width="40" height="40" viewBox="0 0 40 40" className="absolute" style={{ ...p, transform: `rotate(${p.rotate}deg)` }}>
          <path d="M2 20 Q 2 2 20 2" fill="none" stroke="var(--yellow)" strokeWidth="2" />
          <circle cx="2" cy="2" r="3" fill="var(--yellow)" />
        </svg>
      ))}

      {/* Watermark */}
      <div className="absolute font-serif font-bold pointer-events-none"
        style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(-12deg)', fontSize: 220, color: 'rgba(240,184,12,0.06)', letterSpacing: '-0.04em' }}>m</div>

      <div className="relative text-center" style={{ paddingTop: 16 }}>
        <div className="flex justify-center items-center gap-3 mb-6">
          <div className="inline-flex items-center justify-center font-serif font-bold leading-none"
            style={{ width: 48, height: 48, background: 'var(--yellow)', borderRadius: 4, fontSize: 30, color: 'var(--blue)' }}>m</div>
          <div className="text-left">
            <div className="font-serif font-bold leading-none" style={{ fontSize: 22, color: 'var(--yellow)', letterSpacing: '-0.01em' }}>Lincoln</div>
            <div className="font-serif font-bold leading-none" style={{ fontSize: 22, color: 'var(--ink)', letterSpacing: '-0.01em' }}>School</div>
          </div>
        </div>

        <div className="font-bold tracking-widest mb-5" style={{ fontSize: 13, color: 'var(--muted)' }}>CERTIFICATE OF COMPLETION</div>
        <div className="mb-3.5" style={{ fontSize: 15, color: 'var(--ink-2)' }}>This is to certify that</div>
        <h2 className="font-serif font-semibold mb-5" style={{ fontSize: 56, color: 'var(--ink)', letterSpacing: '-0.02em' }}>Imran Ahmed</h2>
        <div className="mb-3" style={{ fontSize: 15, color: 'var(--ink-2)' }}>has successfully completed</div>
        <h3 className="font-serif italic font-medium mb-4" style={{ fontSize: 30, color: 'var(--blue)' }}>AI Literacy 101</h3>

        <p className="mx-auto leading-relaxed" style={{ fontSize: 14, color: 'var(--muted)', maxWidth: 460 }}>
          A 24-lesson program covering AI fundamentals, neural networks, ethics, and voice-AI applications — co-designed with the World Economic Forum.
        </p>

        <div className="grid items-center mt-10" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 24 }}>
          <div className="text-center">
            <div className="italic" style={{ fontFamily: '"Brush Script MT", cursive', fontSize: 24, color: 'var(--ink)', marginBottom: -4 }}>S. Qureshi</div>
            <div className="font-bold pt-2" style={{ borderTop: '1.5px solid var(--ink)', fontSize: 12, color: 'var(--ink)' }}>SALEEM QURESHI</div>
            <div style={{ fontSize: 10, color: 'var(--muted)' }}>Co-founder, Lincoln School</div>
          </div>

          <div className="relative flex justify-center">
            <div className="relative inline-flex items-center justify-center border-2 border-white rounded-full"
              style={{
                width: 96, height: 96,
                background: 'radial-gradient(circle, var(--yellow) 0%, #d99a06 100%)',
                boxShadow: 'inset 0 0 0 4px rgba(255,255,255,0.4), 0 8px 20px rgba(240,184,12,0.4)',
                outline: '2px solid var(--yellow)',
              }}>
              <svg viewBox="0 0 96 96" className="absolute inset-0">
                <defs>
                  <path id="cert-seal-path" d="M 48 48 m -36 0 a 36 36 0 1 1 72 0 a 36 36 0 1 1 -72 0" />
                </defs>
                <text fontSize="8" fontFamily="Fraunces, serif" fontWeight="700" fill="var(--navy)" letterSpacing="3">
                  <textPath href="#cert-seal-path" startOffset="0">LINCOLN SCHOOL · CERTIFIED · LINCOLN SCHOOL · CERTIFIED · </textPath>
                </text>
              </svg>
              <div className="font-serif font-bold leading-none" style={{ fontSize: 32, color: 'var(--navy)' }}>m</div>
            </div>
          </div>

          <div className="flex flex-col items-center">
            <QRCode size={80} />
            <div className="font-mono mt-1.5" style={{ fontSize: 10, color: 'var(--muted)' }}>LIN-2026-08412</div>
            <div style={{ fontSize: 9, color: 'var(--muted-2)' }}>Scan to verify</div>
          </div>
        </div>

        <div className="flex justify-between mt-6" style={{ fontSize: 11, color: 'var(--muted)' }}>
          <span>Helsinki · March 14, 2026</span>
          <span>Co-issued with World Economic Forum</span>
        </div>
      </div>
    </div>
  );
}
